<template>
  <EnterViewportDetector class="m-auto block size-full">
    <template #default="{ inViewport }">
      <RevCarousel
        :alternative-button-label="i18n(translations.alternativeButtonLabel)"
        :alternative-next="i18n(translations.alternativeNext)"
        :alternative-previous="i18n(translations.alternativePrevious)"
        :autoplay="inViewport ? FF_CMS_SLIDER_AUTOPLAY === true : false"
        class="relative m-auto block"
        :class="{
          'w-full lg:max-w-[125rem]': isFullWidth,
          'md:px-24 lg:max-w-[1120px] lg:px-0': !isFullWidth,
        }"
        current-index-id="cms-carousel"
        :tracking="trackingData"
      >
        <component
          :is="slide.tag"
          v-for="(slide, index) in sliderSlides"
          :key="slide.key"
          class="h-full w-full"
          :rel="slide.link?.rel"
          :target="slide.link?.target"
          :to="slide.link?.href"
          @click="slide.handleSlideClick"
        >
          <SliderWrapper v-bind="slide" :is-full-width>
            <ImageRenderer
              :alt="slide.image?.alt"
              class="w-full"
              :fetchpriority="index === 0 ? 'high' : 'low'"
              :height="slide.image?.height"
              :height-mobile="slide.image?.heightMobile"
              :loading="slide.isLoading"
              :media-max-width="MediaMaxWidth.SM"
              :src="slide.image?.src"
              :src-mobile="slide.image?.srcMobile"
              :width="slide.image?.width"
              :width-mobile="slide.image?.widthMobile"
            />
          </SliderWrapper>
        </component>
      </RevCarousel>
    </template>
  </EnterViewportDetector>
</template>

<script setup lang="ts">
import { useRoute, useRuntimeConfig } from '#imports'
import { computed } from 'vue'

import type { SliderApiProps } from '@backmarket/http-api/src/api-specs-content/models/slider'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import type { CarouselData } from '@backmarket/nuxt-module-tracking/types'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevCarousel } from '@ds/components/Carousel'

import {
  Loading,
  MediaMaxWidth,
} from '@backmarket/nuxt-layer-cms/ImageRenderer.constants'
import ImageRenderer from '@backmarket/nuxt-layer-cms/ImageRenderer.vue'
import type { BlockProps } from '@backmarket/nuxt-layer-cms/models/content-block'

import EnterViewportDetector from '../../shared-components/EnterViewportDetector/EnterViewportDetector.vue'

import translations from './Slider.translations'
import SliderWrapper from './SliderWrapper.vue'

const i18n = useI18n()
const route = useRoute()
const { trackCarouselClick } = useTracking()
const { FF_CMS_SLIDER_AUTOPLAY } = useRuntimeConfig().public

export type SliderBlockProps = SliderApiProps & BlockProps
const props = withDefaults(defineProps<SliderBlockProps>(), {
  isFullWidth: true,
})

// This event is common to every CMS block in order to hide the block containers
// on demand
defineEmits(['error'])

const trackSlideClick =
  (slide: SliderApiProps['slides'][0], index: number) => () => {
    trackCarouselClick({
      ...(props.tracking || {}),
      creative: slide.technicalName || '',
      name: String(route.name),
      position: `${String(route.name)}_${index + 1}`,
    })
  }

const sliderSlides = computed(() => {
  return props.slides.map((slide, index) => {
    const hasLink = !isEmpty(slide.link)

    return {
      ...slide,
      tag: hasLink ? RevButtonBase : 'div',
      key: slide.image?.src,
      target: slide.link?.target,
      isLoading: index === 0 ? Loading.Eager : Loading.Lazy,
      handleSlideClick: hasLink ? trackSlideClick(slide, index) : undefined,
    }
  })
})

const trackingData = computed<Omit<CarouselData, 'currentSlide'>>(() => {
  return {
    ...(props.tracking || {}),
    name: String(route.name),
    creatives: props.slides.map((slide) => ({
      creative: slide.technicalName || '',
    })),
  }
})
</script>
